import { useLayoutEffect, useRef } from 'react'
import { NavLink, useNavigate, useLocation } from 'react-router'
import { useAnmeldung } from '../lib/anmeldung'
import type { Werkzeug } from '../lib/api'
import { Profilmenue } from './Profilmenue'

const beschriftung: Record<Werkzeug, string> = {
  bwa: 'BWA',
  jahresabschluss: 'Jahresabschluss',
}

/**
 * Die Reiterzeile benennt die Seite — deshalb tragen die Seiten selbst keine
 * Ueberschrift mehr. Aktionen gehoeren nicht hierher: sie gehoeren zu der
 * Liste, auf die sie wirken.
 */
export function Kopfzeile({ werkzeug }: { werkzeug: Werkzeug }) {
  const { auskunft } = useAnmeldung()
  const navigiere = useNavigate()
  const ort = useLocation()
  const kopf = useRef<HTMLElement>(null)

  /* Die Hoehe der Kopfzeile ist keine Konstante — sie haengt an der
     Schriftgroesse und bricht auf schmalen Fenstern um. Alles, was
     darunter am Fensterrand kleben soll, braucht sie als Mass, und
     geraten wird sie nicht. */
  useLayoutEffect(() => {
    const kasten = kopf.current
    if (!kasten) return
    const messen = () =>
      document.documentElement.style.setProperty('--kopf-hoehe', `${kasten.offsetHeight}px`)
    messen()
    const beobachter = new ResizeObserver(messen)
    beobachter.observe(kasten)
    return () => beobachter.disconnect()
  }, [auskunft])

  if (!auskunft) return null

  /**
   * Beim Umschalten bleibt die Stelle erhalten, an der man gerade ist —
   * einschließlich des geöffneten Mandanten. Wer beim Mandanten die BWA
   * ansieht und auf Jahresabschluss wechselt, will dessen Abschlüsse
   * sehen und nicht wieder von vorn anfangen.
   */
  const wechseln = (ziel: Werkzeug) => {
    const rest = ort.pathname.split('/').slice(2).join('/')
    navigiere(`/${ziel}/${rest || 'eingang'}${ort.search}`)
  }

  return (
    <header className="kopf" ref={kopf}>
      <div className="kopf-oben">
        <div className="wortmarke">
          H&amp;K <span>Automation</span>
        </div>

        {auskunft.werkzeuge.length > 1 ? (
          <div className="umschalter" role="group" aria-label="Werkzeug">
            {auskunft.werkzeuge.map((w) => (
              <button key={w} type="button" aria-pressed={w === werkzeug} onClick={() => wechseln(w)}>
                {beschriftung[w]}
              </button>
            ))}
          </div>
        ) : (
          <div className="kopf-luecke" />
        )}

        <Profilmenue />
      </div>

      <div className="kopf-unten">
        <nav className="reiter">
          <NavLink to={`/${werkzeug}/eingang`} className={({ isActive }) => (isActive ? 'aktiv' : '')}>
            Eingang
          </NavLink>
          <NavLink to={`/${werkzeug}/mandanten`} className={({ isActive }) => (isActive ? 'aktiv' : '')}>
            Mandanten
          </NavLink>
          {auskunft.benutzer.rolle === 'verwaltung' && (
            <NavLink to={`/${werkzeug}/rueckmeldungen`} className={({ isActive }) => (isActive ? 'aktiv' : '')}>
              Rückmeldungen
            </NavLink>
          )}
        </nav>
      </div>
    </header>
  )
}
